import { AxiosInstance } from 'axios';
import { getScimConfig } from '../utils/envUtils';
import { getAxiosInstance } from './axiosInstance';

/** Maximum allowed length for user settings keys (enforced by API and validateUserSettingsKey). */
export const USER_SETTINGS_KEY_MAX_LENGTH = 48;

const BASE_PATH = '/mgmt/usersettings';

/**
 * User settings key constants and intended usage.
 * Keys must be ≤ 48 characters (see validateUserSettingsKey).
 */

/** Format string for search key: one key per entity holding both simple and advanced. Use getSearchKey(entityName). */
export const USER_SETTINGS_SEARCH_KEY_PATTERN = 'search_{entityName}' as const;

/** Stored value for search_{entityName}: simple (query) and adv (filter string). */
export interface SearchSettingValue {
    simple?: string;
    /** Simple-search custom filter text (textarea mode). */
    simpleCustom?: string;
    /** Advanced expression tab query recall. */
    expressionQuery?: string;
    /** Advanced freetext tab query recall. */
    freetextQuery?: string;
    adv?: string;
    /** Last active search mode used by the user for this entity table. */
    lastSearchType?: 'simple' | 'simpleCustom' | 'advanced';
    /** When true, allow combining simple searchtext + advanced filter in a single request. */
    combined?: boolean;
    /** Simple-search-only: whether "Expression" (omit searchdialect=expression) is enabled. */
    expression?: boolean;
    /** Simple-search-only: Search In attributes to restore (when not default). */
    searchIn?: string[];
}

/**
 * Builds the user settings key for search (simple + advanced) for a given entity.
 * @param entityName - Entity type (e.g. "Person", "Group"). Will be lowercased for the key.
 * @returns Key to use, e.g. "search_group". Must be ≤ 48 chars.
 */
export function getSearchKey(entityName: string): string {
    const normalized = (entityName || '').trim().toLowerCase().replace(/\s+/g, '') || 'default';
    const key = `search_${normalized}`;
    validateUserSettingsKey(key);
    return key;
}

/**
 * Validates a user settings key: non-empty and ≤ 48 characters.
 * @param key - The key to validate.
 * @throws Error if key is empty or longer than 48 characters.
 */
export function validateUserSettingsKey(key: string): void {
    if (key == null || typeof key !== 'string') {
        throw new Error('User settings key must be a non-null string');
    }
    const trimmed = key.trim();
    if (trimmed.length === 0) {
        throw new Error('User settings key cannot be empty');
    }
    if (trimmed.length > USER_SETTINGS_KEY_MAX_LENGTH) {
        throw new Error(
            `User settings key must be ${USER_SETTINGS_KEY_MAX_LENGTH} characters or less (got ${trimmed.length})`
        );
    }
}

/**
 * Returns true if the key is valid (non-empty, ≤ 48 chars); false otherwise.
 */
export function isValidUserSettingsKey(key: string): boolean {
    if (key == null || typeof key !== 'string') return false;
    const trimmed = key.trim();
    return trimmed.length > 0 && trimmed.length <= USER_SETTINGS_KEY_MAX_LENGTH;
}

export interface UserSettingItem {
    key: string;
    value: string;
}

export interface UserSettingsGetAllResponse {
    /** All user settings; shape may vary by API (e.g. array of { key, value } or record). */
    [key: string]: unknown;
}

export class UserSettingsService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
    }

    /**
     * Response shape: { [key]: { value: string | object } } or { value: string }. Returns raw value (string or object).
     */
    private static extractValueRaw(data: unknown, key: string): unknown {
        if (data == null) return null;
        if (typeof data === 'string') return data;
        if (typeof data !== 'object') return null;
        const obj = data as Record<string, unknown>;
        const nested = obj[key] ?? obj.value ?? obj.Value;
        if (nested != null && typeof nested === 'object' && 'value' in nested) {
            return (nested as { value?: unknown }).value ?? null;
        }
        if (typeof nested === 'string') return nested;
        return obj.value ?? obj.Value ?? null;
    }

    /**
     * GET api/scim/v2/mgmt/usersettings/{Key}
     * Fetches a single user setting by key.
     * Handles response shape: { [key]: { value: string } } or { value: string }.
     */
    async get(key: string): Promise<string | null> {
        const raw = await this.getRaw(key);
        if (raw == null) return null;
        if (typeof raw === 'string') return raw;
        if (typeof raw === 'object') return JSON.stringify(raw);
        return null;
    }

    /**
     * GET raw value (string or object) for a key. Used by getSearch to handle JSON or object value.
     * Treats 204 No Content (key not present) and 404 as "no value" and returns null.
     */
    async getRaw(key: string): Promise<unknown> {
        validateUserSettingsKey(key);
        const path = `${BASE_PATH}/${encodeURIComponent(key.trim())}`;
        try {
            const response = await this.api.get<Record<string, unknown>>(path);
            if (response.status === 204 || response.data == null) return null;
            return UserSettingsService.extractValueRaw(response.data, key.trim());
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 404 || status === 204) return null;
            throw err;
        }
    }

    /**
     * If the string is double-encoded JSON (e.g. "\"foo\"" with backslash-escaped inner quotes), parse once and return the inner string.
     * Prevents extra quotes accumulating when the value is stored/returned double-encoded.
     * Only unquotes when the second character is backslash (clear double-encoding); leaves user search terms that intentionally
     * start/end with quotes (e.g. "exact phrase") unchanged.
     */
    private static unquoteIfJsonString(s: string): string {
        if (typeof s !== 'string' || s.length < 4 || s[0] !== '"' || s[s.length - 1] !== '"' || s[1] !== '\\') return s;
        try {
            const parsed = JSON.parse(s);
            return typeof parsed === 'string' ? parsed : s;
        } catch {
            return s;
        }
    }

    /**
     * Fetches the combined search setting (simple + adv) for an entity.
     * Value may be stored as JSON string or object; returns normalized { simple?, adv? }.
     * Unquotes simple/adv when they are JSON-quoted strings to prevent extra quotes on reload.
     */
    async getSearch(entityName: string): Promise<SearchSettingValue | null> {
        const key = getSearchKey(entityName);
        const raw = await this.getRaw(key);
        if (raw == null) return null;
        const normalize = (val: unknown): SearchSettingValue | null => {
            if (val == null) return null;
            if (typeof val !== 'object') return null;
            const o = val as Record<string, unknown>;
            const expressionRaw = o.expression ?? o.Expression;
            const combinedRaw = o.combined ?? o.Combined;
            const searchInRaw = o.searchIn ?? o.searchin ?? o.SearchIn;
            const lastSearchTypeRaw = o.lastSearchType ?? o.lastsearchtype ?? o.LastSearchType;
            const expression = typeof expressionRaw === 'boolean'
                ? expressionRaw
                : (typeof expressionRaw === 'string'
                    ? expressionRaw.trim().toLowerCase() === 'true'
                    : undefined);
            const combined = typeof combinedRaw === 'boolean'
                ? combinedRaw
                : (typeof combinedRaw === 'string'
                    ? combinedRaw.trim().toLowerCase() === 'true'
                    : undefined);
            const normalizedLastSearchType = typeof lastSearchTypeRaw === 'string'
                ? lastSearchTypeRaw.trim()
                : undefined;
            const lastSearchType = (normalizedLastSearchType === 'simple' ||
                normalizedLastSearchType === 'simpleCustom' ||
                normalizedLastSearchType === 'advanced')
                ? normalizedLastSearchType
                : undefined;
            let searchIn: string[] | undefined;
            if (Array.isArray(searchInRaw)) {
                searchIn = (searchInRaw as unknown[]).map((x) => String(x)).filter(Boolean);
            } else if (typeof searchInRaw === 'string' && searchInRaw.trim()) {
                // Accept comma-separated strings defensively
                searchIn = searchInRaw.split(',').map((s) => s.trim()).filter(Boolean);
            }
            const simpleRaw = o.simple != null ? String(o.simple) : undefined;
            const simpleCustomRaw = o.simpleCustom != null ? String(o.simpleCustom) : undefined;
            const expressionQueryRaw = o.expressionQuery != null ? String(o.expressionQuery) : (o.expressionquery != null ? String(o.expressionquery) : undefined);
            const freetextQueryRaw = o.freetextQuery != null ? String(o.freetextQuery) : (o.freetextquery != null ? String(o.freetextquery) : undefined);
            const advRaw = o.adv != null ? String(o.adv) : undefined;
            return {
                simple: simpleRaw != null ? UserSettingsService.unquoteIfJsonString(simpleRaw) : undefined,
                simpleCustom: simpleCustomRaw != null ? UserSettingsService.unquoteIfJsonString(simpleCustomRaw) : undefined,
                expressionQuery: expressionQueryRaw != null ? UserSettingsService.unquoteIfJsonString(expressionQueryRaw) : undefined,
                freetextQuery: freetextQueryRaw != null ? UserSettingsService.unquoteIfJsonString(freetextQueryRaw) : undefined,
                adv: advRaw != null ? UserSettingsService.unquoteIfJsonString(advRaw) : undefined,
                lastSearchType,
                combined,
                expression,
                searchIn
            };
        };
        if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw) as unknown;
                if (typeof parsed === 'string') {
                    return { simple: UserSettingsService.unquoteIfJsonString(parsed), adv: undefined };
                }
                return normalize(parsed) ?? { simple: UserSettingsService.unquoteIfJsonString(raw), adv: undefined };
            } catch {
                return { simple: UserSettingsService.unquoteIfJsonString(raw), adv: undefined };
            }
        }
        if (typeof raw === 'object' && raw !== null) {
            return normalize(raw);
        }
        return null;
    }

    /**
     * Saves simple and/or adv for an entity. When mergeWithExisting is true, GETs current value and merges.
     */
    async setSearch(entityName: string, updates: SearchSettingValue, mergeWithExisting: boolean): Promise<void> {
        const key = getSearchKey(entityName);
        let value: SearchSettingValue = updates;
        if (mergeWithExisting) {
            const current = (await this.getSearch(entityName)) ?? {};
            // Spread merge so callers can intentionally clear keys by providing undefined.
            value = { ...current, ...updates };
        }
        await this.set(key, value as Record<string, unknown>);
    }

    /**
     * GET api/scim/v2/mgmt/usersettings
     * Fetches all user settings for the current user.
     * Handles response shape: { [key]: { value: string }, ... }. Treats 204 No Content as empty list.
     */
    async getAll(): Promise<UserSettingItem[]> {
        const response = await this.api.get<Record<string, { value?: string } | string>>(BASE_PATH);
        if (response.status === 204 || response.data == null) return [];
        const data = response.data;
        const valueToDisplay = (raw: unknown): string => {
            if (raw == null) return '';
            if (typeof raw === 'string') return raw;
            if (typeof raw === 'object') return JSON.stringify(raw);
            return String(raw);
        };
        if (Array.isArray(data)) {
            return data.map((item: unknown) => {
                const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
                const key = typeof o.key === 'string' ? o.key : '';
                const val = UserSettingsService.extractValueRaw(o, key);
                return { key, value: valueToDisplay(val) };
            });
        }
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            return Object.entries(data).map(([k, v]) => {
                const raw = (v != null && typeof v === 'object' && 'value' in v)
                    ? (v as { value: unknown }).value
                    : v;
                return { key: k, value: valueToDisplay(raw) };
            });
        }
        return [];
    }

    /**
     * POST api/scim/v2/mgmt/usersettings/{Key}
     * Creates or updates a user setting. Value can be string or object (e.g. { simple, adv }).
     */
    async set(key: string, value: string | Record<string, unknown>): Promise<void> {
        validateUserSettingsKey(key);
        const path = `${BASE_PATH}/${encodeURIComponent(key.trim())}`;
        await this.api.post(path, { value });
    }

    /**
     * DELETE api/scim/v2/mgmt/usersettings/{Key}
     * Removes a user setting by key.
     */
    async delete(key: string): Promise<void> {
        validateUserSettingsKey(key);
        const path = `${BASE_PATH}/${encodeURIComponent(key.trim())}`;
        await this.api.delete(path);
    }
}

let defaultInstance: UserSettingsService | null = null;

/**
 * Shared instance for user settings API calls.
 */
export function getUserSettingsService(): UserSettingsService {
    if (!defaultInstance) {
        defaultInstance = new UserSettingsService();
    }
    return defaultInstance;
}
